<section class="metrics-shell">
  <header class="dashboard-header">
    <div>
      <p class="eyebrow"><span class="eyebrow-mark"></span> Operations Intelligence</p>
      <h1>Metrics Dashboard</h1>
      <p class="dashboard-subtitle">A clear view of today’s workload and the service activity behind it.</p>
    </div>
    <div class="header-actions">
      <div class="updated-at" *ngIf="metrics">
        <span class="live-dot"></span>
        <span>Updated {{ metrics.generatedAt | date: 'h:mm a' }}</span>
      </div>
      <button mat-flat-button color="primary" class="refresh-button" type="button" (click)="refresh()" [disabled]="loading" [attr.aria-busy]="loading">
        <mat-icon class="refresh-icon" [class.spin]="loading" aria-hidden="true">refresh</mat-icon>
        {{ loading ? 'Refreshing' : 'Refresh' }}
      </button>
    </div>
  </header>

  <mat-card appearance="outlined" class="filter-panel" role="region" aria-label="Dashboard date range">
    <div class="filter-topline">
      <div>
        <p class="panel-kicker">Activity Period</p>
        <p class="filter-caption">Choose a window for incoming complaints, categories and areas.</p>
      </div>
      <mat-form-field appearance="outline" class="period-selector" subscriptSizing="dynamic">
        <mat-label>Activity Period</mat-label>
        <mat-select [value]="selectedRange" (selectionChange)="selectRange($event.value)">
          <mat-option *ngFor="let period of periods" [value]="period.value">{{ period.label }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <div class="custom-range" *ngIf="selectedRange === 'custom'">
      <mat-form-field appearance="outline" class="date-range-field">
        <mat-label>Activity Date Range</mat-label>
        <mat-date-range-input [formGroup]="customRangeForm" [rangePicker]="rangePicker" [max]="maxDate">
          <input matStartDate formControlName="fromDate" placeholder="Start Date" aria-label="Start Date">
          <input matEndDate formControlName="toDate" placeholder="End Date" aria-label="End Date">
        </mat-date-range-input>
        <mat-datepicker-toggle matIconSuffix [for]="rangePicker"></mat-datepicker-toggle>
        <mat-date-range-picker #rangePicker></mat-date-range-picker>
        <mat-error *ngIf="customRangeForm.controls.fromDate.hasError('required')">Choose a start date.</mat-error>
        <mat-error *ngIf="customRangeForm.controls.fromDate.hasError('matStartDateInvalid')">Enter a valid start date.</mat-error>
        <mat-error *ngIf="customRangeForm.controls.toDate.hasError('required')">Choose an end date.</mat-error>
        <mat-error *ngIf="customRangeForm.controls.toDate.hasError('matEndDateInvalid')">Enter a valid end date.</mat-error>
      </mat-form-field>
      <button mat-flat-button color="primary" class="apply-button" type="button" (click)="applyCustomRange()" [disabled]="loading">
        <mat-icon aria-hidden="true">check</mat-icon>
        Apply Range
      </button>
    </div>

    <div class="filter-footnote">
      <mat-icon aria-hidden="true">info</mat-icon>
      <span>Open work, due dates and assignments are live snapshots. The selected period filters incoming activity.</span>
      <span class="active-range" *ngIf="metrics">{{ metrics.rangeLabel | titlecase }}</span>
    </div>
  </mat-card>

  <div class="dashboard-error" role="alert" *ngIf="errorMessage">
    <mat-icon aria-hidden="true">warning</mat-icon>
    <span>{{ errorMessage }}</span>
    <button mat-button color="warn" type="button" (click)="refresh()">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      Try Again
    </button>
  </div>

  <mat-card appearance="raised" class="initial-loading" *ngIf="loading && !metrics" role="status" aria-live="polite">
    <mat-progress-spinner mode="indeterminate" diameter="24" strokeWidth="3" aria-label="Loading dashboard"></mat-progress-spinner>
    <span>Preparing your dashboard…</span>
  </mat-card>

  <ng-container *ngIf="metrics as dashboard">
    <!-- The API includes this block only for admins; pending is live while decisions follow the selected period. -->
    <section class="admin-request-summary" *ngIf="dashboard.userRequestMetrics" aria-labelledby="admin-request-heading">
      <div class="section-heading">
        <div>
          <p class="panel-kicker">Admin Overview</p>
          <h2 id="admin-request-heading">Resident Registration Requests</h2>
        </div>
        <span class="section-context">Pending is current; decisions use the selected period</span>
      </div>
      <div class="summary-grid request-summary-grid" aria-label="User request summary">
        <mat-card appearance="raised" class="summary-card" role="article"
          *ngFor="let card of requestSummaryCards; trackBy: trackSummaryCard"
          [ngClass]="'tone-' + card.tone">
          <div class="summary-card-top">
            <span class="summary-icon"><mat-icon aria-hidden="true">{{ card.icon }}</mat-icon></span>
            <span class="summary-badge">{{ card.badge }}</span>
          </div>
          <p class="summary-value">{{ card.value | number }}</p>
          <h2>{{ card.label }}</h2>
          <p class="summary-note">{{ card.note | titlecase }}</p>
        </mat-card>
      </div>
    </section>

    <section class="request-analytics-grid" *ngIf="dashboard.userRequestMetrics as requestMetrics"
      aria-label="User request analytics">
      <mat-card appearance="raised" class="chart-card request-trend-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>person_add</mat-icon></span>
          <div>
            <p class="panel-kicker">Volume</p>
            <h3>Incoming User Requests</h3>
            <p class="card-description">Resident registrations received in the selected period</p>
          </div>
          <div class="chart-total">
            <span>Total Received</span>
            <strong>{{ requestTrendTotal | number }}</strong>
          </div>
        </div>
        <div class="trend-chart" *ngIf="requestMetrics.requestTrend.length; else noIncomingRequests">
          <svg viewBox="0 0 760 230" role="group"
            [attr.aria-label]="'Incoming user request trend for ' + dashboard.rangeLabel">
            <defs>
              <linearGradient id="requestTrendFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stop-color="#169988" stop-opacity="0.22"></stop>
                <stop offset="100%" stop-color="#169988" stop-opacity="0.01"></stop>
              </linearGradient>
            </defs>
            <g class="chart-axis">
              <ng-container *ngFor="let tick of requestTrendTicks">
                <line
                  [attr.x1]="chartPaddingLeft"
                  [attr.x2]="chartWidth - chartPaddingRight"
                  [attr.y1]="trendTickY(tick, requestTrendMaxValue)"
                  [attr.y2]="trendTickY(tick, requestTrendMaxValue)">
                </line>
                <text [attr.x]="chartPaddingLeft - 12"
                  [attr.y]="trendTickY(tick, requestTrendMaxValue) + 4">{{ tick }}</text>
              </ng-container>
            </g>
            <path class="trend-area" [attr.d]="requestTrendAreaPath"></path>
            <path class="trend-line" [attr.d]="requestTrendLinePath"></path>
            <g class="trend-point" *ngFor="let point of requestTrendGraphPoints; trackBy: trackTrendPoint">
              <circle class="trend-dot" [class.active]="activeRequestTrendPoint?.key === point.key"
                [attr.cx]="point.x" [attr.cy]="point.y" r="3.5"></circle>
              <text *ngIf="point.showLabel" [attr.x]="point.x" y="218">{{ point.label }}</text>
            </g>
            <rect *ngFor="let point of requestTrendGraphPoints; trackBy: trackTrendPoint" class="trend-hit-area"
              [attr.x]="point.hitX" [attr.y]="chartPaddingTop - 8"
              [attr.width]="point.hitWidth" [attr.height]="chartHeight - chartPaddingTop - chartPaddingBottom + 16"
              [attr.aria-label]="requestTrendTooltip(point)" role="img" tabindex="0"
              (mouseenter)="showRequestTrendPoint(point)" (mouseleave)="hideRequestTrendPoint()"
              (focus)="showRequestTrendPoint(point)" (blur)="hideRequestTrendPoint()"
              (touchstart)="showRequestTrendPoint(point)" (click)="showRequestTrendPoint(point)"
              (keydown.escape)="hideRequestTrendPoint()"></rect>
            <g *ngIf="activeRequestTrendPoint as point" class="trend-tooltip" aria-hidden="true"
              [attr.transform]="'translate(' + trendTooltipX(point) + ',' + trendTooltipY(point) + ')'">
              <rect width="190" height="64" rx="8"></rect>
              <text class="trend-tooltip-period" x="12" y="22">{{ trendPeriod(point) }}</text>
              <text class="trend-tooltip-count" x="12" y="47">{{ trendCountLabel(point, 'user request') }}</text>
            </g>
          </svg>
        </div>
        <p class="trend-hint" *ngIf="requestMetrics.requestTrend.length">
          <mat-icon aria-hidden="true">touch_app</mat-icon> Hover, focus or tap the chart for details.
        </p>
        <ng-template #noIncomingRequests>
          <div class="chart-empty">
            <span class="empty-icon"><mat-icon aria-hidden="true">person_add</mat-icon></span>
            <p>No incoming user requests</p>
            <span>No registration requests were received during this period.</span>
          </div>
        </ng-template>
      </mat-card>

      <mat-card appearance="raised" class="chart-card request-status-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>how_to_reg</mat-icon></span>
          <div>
            <p class="panel-kicker">Request Status</p>
            <h3>Pending, Approved and Rejected</h3>
            <p class="card-description">Current pending status; decisions based on the selected period.</p>
          </div>
        </div>
        <div class="request-status-content">
          <div class="donut-chart">
            <svg viewBox="0 0 120 120" role="img" aria-label="Pending, approved and rejected user request counts">
              <circle class="donut-track" cx="60" cy="60" r="43"></circle>
              <circle
                *ngFor="let segment of requestStatusSegments"
                class="donut-segment"
                cx="60"
                cy="60"
                r="43"
                [attr.stroke]="segment.color"
                [attr.stroke-dasharray]="segment.dashArray"
                [attr.stroke-dashoffset]="segment.dashOffset">
              </circle>
              <text class="donut-value request-donut-value" x="60" y="58">REQ</text>
              <text class="donut-label" x="60" y="75">STATUS</text>
            </svg>
          </div>
          <div class="status-legend request-status-legend">
            <div class="legend-row" *ngFor="let point of requestStatusPoints">
              <span class="legend-dot" [style.background]="point.color"></span>
              <span class="legend-name">
                <span>{{ point.label }}</span>
                <small>{{ point.scopeLabel }}</small>
              </span>
              <strong>{{ point.value | number }}</strong>
            </div>
          </div>
        </div>
      </mat-card>
    </section>

    <section class="summary-grid" aria-label="Complaint summary">
      <mat-card appearance="raised" class="summary-card" role="article" *ngFor="let card of summaryCards; trackBy: trackSummaryCard" [ngClass]="'tone-' + card.tone">
        <div class="summary-card-top">
          <span class="summary-icon"><mat-icon aria-hidden="true">{{ card.icon }}</mat-icon></span>
          <span class="summary-badge">{{ card.badge }}</span>
        </div>
        <p class="summary-value">{{ card.value | number }}</p>
        <h2>{{ card.label }}</h2>
        <p class="summary-note">{{ card.note }}</p>
      </mat-card>
    </section>

    <div class="section-heading">
      <div>
        <p class="panel-kicker">Service Activity</p>
        <h2>What’s Moving</h2>
      </div>
      <span class="section-context">{{ dashboard.rangeLabel | titlecase }} · incoming complaints</span>
    </div>

    <section class="chart-grid" aria-label="Dashboard charts">


      <mat-card appearance="raised" class="chart-card trend-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>show_chart</mat-icon></span>
          <div>
            <p class="panel-kicker">Volume</p>
            <h3>Incoming Complaints</h3>
            <p class="card-description">Complaint lines received in the selected period</p>
          </div>
          <div class="chart-total">
            <span>Total Received</span>
            <strong>{{ dashboard.summary.newComplaintsInRange | number }}</strong>
          </div>
        </div>
        <div class="trend-chart" *ngIf="dashboard.complaintTrend.length; else noIncoming">
          <svg viewBox="0 0 760 230" role="group" [attr.aria-label]="'Incoming complaint trend for ' + dashboard.rangeLabel">
            <defs>
              <linearGradient id="trendFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stop-color="#1765b8" stop-opacity="0.22"></stop>
                <stop offset="100%" stop-color="#1765b8" stop-opacity="0.01"></stop>
              </linearGradient>
            </defs>
            <g class="chart-axis">
              <ng-container *ngFor="let tick of trendTicks">
                <line
                  [attr.x1]="chartPaddingLeft"
                  [attr.x2]="chartWidth - chartPaddingRight"
                  [attr.y1]="trendTickY(tick)"
                  [attr.y2]="trendTickY(tick)">
                </line>
                <text [attr.x]="chartPaddingLeft - 12" [attr.y]="trendTickY(tick) + 4">{{ tick }}</text>
              </ng-container>
            </g>
            <path class="trend-area" [attr.d]="trendAreaPath"></path>
            <path class="trend-line" [attr.d]="trendLinePath"></path>
            <g class="trend-point" *ngFor="let point of trendGraphPoints; trackBy: trackTrendPoint">
              <circle class="trend-dot" [class.active]="activeTrendPoint?.key === point.key"
                [attr.cx]="point.x" [attr.cy]="point.y" r="3.5"></circle>
              <text *ngIf="point.showLabel" [attr.x]="point.x" y="218">{{ point.label }}</text>
            </g>
            <rect *ngFor="let point of trendGraphPoints; trackBy: trackTrendPoint" class="trend-hit-area"
              [attr.x]="point.hitX" [attr.y]="chartPaddingTop - 8"
              [attr.width]="point.hitWidth" [attr.height]="chartHeight - chartPaddingTop - chartPaddingBottom + 16"
              [attr.aria-label]="trendTooltip(point)" role="img" tabindex="0"
              (mouseenter)="showTrendPoint(point)" (mouseleave)="hideTrendPoint()"
              (focus)="showTrendPoint(point)" (blur)="hideTrendPoint()"
              (touchstart)="showTrendPoint(point)" (click)="showTrendPoint(point)"
              (keydown.escape)="hideTrendPoint()"></rect>
            <g *ngIf="activeTrendPoint as point" class="trend-tooltip" aria-hidden="true"
              [attr.transform]="'translate(' + trendTooltipX(point) + ',' + trendTooltipY(point) + ')'">
              <rect width="190" height="64" rx="8"></rect>
              <text class="trend-tooltip-period" x="12" y="22">{{ trendPeriod(point) }}</text>
              <text class="trend-tooltip-count" x="12" y="47">{{ point.value | number }} {{ point.value === 1 ? 'complaint' : 'complaints' }}</text>
            </g>
          </svg>
        </div>
        <p class="trend-hint" *ngIf="dashboard.complaintTrend.length">
          <mat-icon aria-hidden="true">touch_app</mat-icon> Hover, focus or tap the chart for details.
        </p>
        <ng-template #noIncoming>
          <div class="chart-empty">
            <span class="empty-icon"><mat-icon aria-hidden="true">show_chart</mat-icon></span>
            <p>No incoming complaints in this period</p>
            <span>Try a wider date range to explore activity.</span>
          </div>
        </ng-template>
      </mat-card>

      <mat-card appearance="raised" class="chart-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>donut_large</mat-icon></span>
          <div>
            <p class="panel-kicker">Current Workload</p>
            <h3>Complaint Status</h3>
            <p class="card-description">Open complaint lines by latest status</p>
          </div>
        </div>
        <div class="status-content" *ngIf="dashboard.complaintStatuses.length; else noStatuses">
          <div class="donut-chart">
            <svg viewBox="0 0 120 120" role="img" aria-label="Complaint status distribution">
              <circle class="donut-track" cx="60" cy="60" r="43"></circle>
              <circle
                *ngFor="let segment of statusSegments"
                class="donut-segment"
                cx="60"
                cy="60"
                r="43"
                [attr.stroke]="segment.color"
                [attr.stroke-dasharray]="segment.dashArray"
                [attr.stroke-dashoffset]="segment.dashOffset">
              </circle>
              <text class="donut-value" x="60" y="58">{{ statusTotal | number }}</text>
              <text class="donut-label" x="60" y="75">OPEN</text>
            </svg>
          </div>
          <div class="status-legend">
            <div class="legend-row" *ngFor="let point of dashboard.complaintStatuses; let i = index">
              <span class="legend-dot" [style.background]="colorForIndex(i)"></span>
              <span class="legend-name">{{ point.label }}</span>
              <strong>{{ point.value | number }}</strong>
            </div>
          </div>
        </div>
        <ng-template #noStatuses>
          <div class="chart-empty compact">
            <span class="empty-icon"><mat-icon aria-hidden="true">pie_chart</mat-icon></span>
            <p>No open complaints</p>
            <span>Your accessible work queue is clear.</span>
          </div>
        </ng-template>
      </mat-card>

      <mat-card appearance="raised" class="chart-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>assignment_turned_in</mat-icon></span>
          <div>
            <p class="panel-kicker">Work in Progress</p>
            <h3>Job Card Status</h3>
            <p class="card-description">Active job cards across your accessible work</p>
          </div>
        </div>
        <div class="bar-list" *ngIf="dashboard.jobCardStatuses.length; else noJobCards">
          <div class="bar-row" *ngFor="let point of dashboard.jobCardStatuses; let i = index">
            <div class="bar-label">
              <span>{{ point.label }}</span>
              <strong>{{ point.value | number }}</strong>
            </div>
            <div class="bar-track">
              <span [style.width.%]="barWidth(point, dashboard.jobCardStatuses)" [style.background]="colorForIndex(i)"></span>
            </div>
          </div>
        </div>
        <ng-template #noJobCards>
          <div class="chart-empty compact">
            <span class="empty-icon"><mat-icon aria-hidden="true">assignment_turned_in</mat-icon></span>
            <p>No active job cards</p>
            <span>There are no active job cards in this scope.</span>
          </div>
        </ng-template>
      </mat-card>

      <mat-card appearance="raised" class="chart-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>account_tree</mat-icon></span>
          <div>
            <p class="panel-kicker">Team Overview</p>
            <h3>Department Workload and SLA</h3>
            <p class="card-description">Current open complaints by department and SLA</p>
          </div>
        </div>
        <ng-container *ngIf="departmentMetrics as departments">
          <div class="department-list" *ngIf="departments.length; else noDepartments"
            role="region" aria-label="Department workload and SLA; scroll to view all departments" tabindex="0">
            <div class="department-row" *ngFor="let item of departments; let i = index; trackBy: trackDepartment">
              <h4>{{ item.department.label }}</h4>
              <div class="department-slas" *ngIf="item.severities.length; else noDepartmentSla">
                <div class="department-sla" *ngFor="let severity of item.severities"
                  [ngClass]="'severity-' + severityTone(severity.label)">
                  <span class="severity-name">
                    <span class="severity-dot" aria-hidden="true"></span>
                    {{ severity.label }}
                  </span>
                  <strong>{{ severity.value | number }}</strong>
                </div>
              </div>
              <ng-template #noDepartmentSla>
                <p class="department-no-sla">No SLA breakdown is available for this department.</p>
              </ng-template>
              <div class="department-total">
                <span>Total Open Complaints</span>
                <strong>{{ item.department.value | number }}</strong>
              </div>
              <div class="bar-track">
                <span [style.width.%]="barWidth(item.department, dashboard.departmentWorkload)" [style.background]="colorForIndex(i)"></span>
              </div>
            </div>
          </div>
          <p class="department-scroll-hint" *ngIf="departments.length > 3">
            <mat-icon aria-hidden="true">unfold_more</mat-icon>
            Scroll to view all {{ departments.length }} departments
          </p>
        </ng-container>
        <ng-template #noDepartments>
          <div class="chart-empty compact">
            <span class="empty-icon"><mat-icon aria-hidden="true">account_tree</mat-icon></span>
            <p>No department workload</p>
            <span>No open work is assigned to these departments.</span>
          </div>
        </ng-template>
      </mat-card>

      <mat-card appearance="raised" class="chart-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>people</mat-icon></span>
          <div>
            <p class="panel-kicker">Resident Profile</p>
            <h3>Resident Priority</h3>
            <p class="card-description">Open complaints by resident category</p>
          </div>
        </div>
        <div class="priority-list" *ngIf="dashboard.residentPriorityDistribution.length; else noResidentPriority">
          <div class="priority-row" *ngFor="let point of dashboard.residentPriorityDistribution; let i = index">
            <span class="priority-mark" [style.background]="colorForIndex(i)"></span>
            <span class="priority-label">{{ point.label }}</span>
            <div class="priority-track">
              <span [style.width.%]="barWidth(point, dashboard.residentPriorityDistribution)" [style.background]="colorForIndex(i)"></span>
            </div>
            <strong>{{ point.value | number }}</strong>
          </div>
        </div>
        <ng-template #noResidentPriority>
          <div class="chart-empty compact">
            <span class="empty-icon"><mat-icon aria-hidden="true">people</mat-icon></span>
            <p>No resident priority data</p>
            <span>Priority mix will appear with active complaints.</span>
          </div>
        </ng-template>
      </mat-card>

      <mat-card appearance="raised" class="chart-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>label</mat-icon></span>
          <div>
            <p class="panel-kicker">Incoming Work</p>
            <h3>Top Categories</h3>
            <p class="card-description">{{ dashboard.rangeLabel | titlecase }} · most reported complaint lines</p>
          </div>
        </div>
        <div class="bar-list ranked-list" *ngIf="dashboard.categoryBreakdown.length; else noCategories">
          <div class="bar-row" *ngFor="let point of dashboard.categoryBreakdown; let i = index">
            <div class="bar-label">
              <span class="rank-label"><em>{{ i + 1 | number: '2.0-0' }}</em>{{ point.label }}</span>
              <strong>{{ point.value | number }}</strong>
            </div>
            <div class="bar-track">
              <span [style.width.%]="barWidth(point, dashboard.categoryBreakdown)" [style.background]="colorForIndex(i)"></span>
            </div>
          </div>
        </div>
        <ng-template #noCategories>
          <div class="chart-empty compact">
            <span class="empty-icon"><mat-icon aria-hidden="true">label</mat-icon></span>
            <p>No category activity</p>
            <span>No complaints were received during this period.</span>
          </div>
        </ng-template>
      </mat-card>

      <mat-card appearance="raised" class="chart-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>place</mat-icon></span>
          <div>
            <p class="panel-kicker">Service Locations</p>
            <h3>Top Areas</h3>
            <p class="card-description">{{ dashboard.rangeLabel | titlecase }} · incoming complaint lines</p>
          </div>
        </div>
        <div class="bar-list ranked-list" *ngIf="dashboard.areaBreakdown.length; else noAreas">
          <div class="bar-row" *ngFor="let point of dashboard.areaBreakdown; let i = index">
            <div class="bar-label">
              <span class="rank-label"><em>{{ i + 1 | number: '2.0-0' }}</em>{{ point.label }}</span>
              <strong>{{ point.value | number }}</strong>
            </div>
            <div class="bar-track">
              <span [style.width.%]="barWidth(point, dashboard.areaBreakdown)" [style.background]="colorForIndex(i)"></span>
            </div>
          </div>
        </div>
        <ng-template #noAreas>
          <div class="chart-empty compact">
            <span class="empty-icon"><mat-icon aria-hidden="true">place</mat-icon></span>
            <p>No area activity</p>
            <span>No complaints were received during this period.</span>
          </div>
        </ng-template>
      </mat-card>

      <mat-card appearance="raised" class="chart-card" role="article">
        <div class="card-heading">
          <span class="card-icon" aria-hidden="true"><mat-icon>person</mat-icon></span>
          <div>
            <p class="panel-kicker">Team Overview</p>
            <h3>Employee Workload</h3>
            <p class="card-description">Top eight staff by current open complaints</p>
          </div>
        </div>
        <div class="bar-list" *ngIf="dashboard.employeeWorkload.length; else noEmployees">
          <div class="bar-row" *ngFor="let point of dashboard.employeeWorkload; let i = index">
            <div class="bar-label">
              <span>{{ point.label }}</span>
              <strong>{{ point.value | number }}</strong>
            </div>
            <div class="bar-track">
              <span [style.width.%]="barWidth(point, dashboard.employeeWorkload)" [style.background]="colorForIndex(i)"></span>
            </div>
          </div>
        </div>
        <ng-template #noEmployees>
          <div class="chart-empty compact">
            <span class="empty-icon"><mat-icon aria-hidden="true">person</mat-icon></span>
            <p>No employee workload</p>
            <span>No open work is assigned to employees in this scope.</span>
          </div>
        </ng-template>
      </mat-card>
    </section>

    <footer class="dashboard-footer">
      <span><mat-icon aria-hidden="true">verified_user</mat-icon> Metrics are limited to your assigned area and department access.</span>
      <span>Activity counts are complaint-line based.</span>
    </footer>
  </ng-container>
</section>
